/**
 * Hook useCurrentUserSeller
 * Verifica se o usuário atual está vinculado a um vendedor (crm_sellers)
 * Usado para exibir alertas de restrição de visualização no CRM
 *
 * CHANGELOG:
 *   2026-06-02 v1.0.1 - hasFullAccess agora considera também a role
 *     'tenant_admin' (via roles[]), não apenas isTenantAdmin do useAuth
 *     (que só é true para owner do tenant ou root_admin). Isso alinha o
 *     Kanban de Negócios com a página "Todos os Negócios", evitando o
 *     "Acesso Restrito" para admins não-owner.
 *   2026-xx-xx v1.0.0 - Versão inicial.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

// Logger para o módulo
const logger = createLogger('CurrentUserSeller');

// Interface do retorno
export interface CurrentUserSeller {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  avatar_url: string | null;
  commission_rate: number | null;
  is_active: boolean;
}

/**
 * Hook para verificar se o usuário atual possui vendedor vinculado
 * Retorna o seller vinculado ou null se não houver
 */
export function useCurrentUserSeller() {
  const { currentTenant, user, roles, isTenantAdmin } = useAuth();

  // Query para buscar seller vinculado ao usuário atual via RPC
  // Usa RPC existente get_current_user_seller_id (mais seguro, sem SELECT direto)
  const sellerQuery = useQuery({
    queryKey: ['current-user-seller', currentTenant?.id, user?.id],
    queryFn: async () => {
      if (!currentTenant?.id || !user?.id) return null;

      // Usa RPC para obter ID do seller vinculado (segurança tenant)
      const { data: sellerId, error } = await supabase.rpc('get_current_user_seller_id', {
        p_tenant_id: currentTenant.id
      });

      if (error) {
        logger.error('Erro ao buscar seller do usuário via RPC:', error.message);
        throw error;
      }

      // Se não há seller vinculado, retorna null
      if (!sellerId) {
        logger.debug('Usuário não possui seller vinculado');
        return null;
      }

      // Retorna objeto mínimo com ID (suficiente para validação)
      // Dados completos são buscados apenas quando necessário
      logger.debug('Seller do usuário atual:', sellerId);
      return { 
        id: sellerId,
        name: '',
        email: null,
        phone: null,
        avatar_url: null,
        commission_rate: null,
        is_active: true,
      } as CurrentUserSeller;
    },
    enabled: !!currentTenant?.id && !!user?.id,
    staleTime: 5 * 60 * 1000, // Cache por 5 minutos (não muda frequentemente)
  });

  // Verifica se usuário é supervisor via roles
  const isSupervisor = roles.some(r => r.role === 'tenant_supervisor');

  // Verifica se usuário possui role tenant_admin (admin não-owner do tenant).
  // isTenantAdmin do useAuth só cobre owner do tenant + root_admin, então
  // precisamos olhar roles[] explicitamente para admins comuns.
  const isTenantAdminRole = roles.some(r => r.role === 'tenant_admin');

  // Verifica se usuário tem acesso total (admin/owner/root/supervisor)
  const hasFullAccess = isTenantAdmin || isTenantAdminRole || isSupervisor;

  // Verifica se usuário precisa de seller vinculado mas não tem
  const needsSellerLink = !hasFullAccess && !sellerQuery.data && !sellerQuery.isLoading;

  return {
    /** Seller vinculado ao usuário atual (ou null) */
    seller: sellerQuery.data,
    /** Se está carregando */
    isLoading: sellerQuery.isLoading,
    /** Erro na query */
    error: sellerQuery.error,
    /** Se usuário tem acesso total (admin/supervisor) */
    hasFullAccess,
    /** Se usuário é agente sem seller vinculado (sem acesso ao CRM) */
    needsSellerLink,
    /** Refetch manual */
    refetch: sellerQuery.refetch,
  };
}
